import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiUpload, FiVideo, FiFileText, FiLogOut, FiArrowLeft, FiHome, FiCamera, FiCheck } from 'react-icons/fi';

const AdminUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSubtitleFile, setSelectedSubtitleFile] = useState(null);
  const [fileType, setFileType] = useState('video'); // video, audio, or document
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('human');
  const [subcategory, setSubcategory] = useState('');
  const [level, setLevel] = useState('Starters');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [vocabularyAnalysis, setVocabularyAnalysis] = useState(null);
  
  const [videoUrl, setVideoUrl] = useState(null);
  const [posterImage, setPosterImage] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [extractingFrame, setExtractingFrame] = useState(false);
  const [selectedPosterFile, setSelectedPosterFile] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();

  // 检查是否是管理员
  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === 'video' && !file.type.startsWith('video/')) {
        setMessage('请选择视频文件');
        setSelectedFile(null);
        return;
      }
      if (fileType === 'audio' && !file.type.startsWith('audio/')) {
        setMessage('请选择音频文件');
        setSelectedFile(null);
        return;
      }
      if (fileType === 'document' && !file.type.includes('pdf') && !file.type.includes('word')) {
        setMessage('请选择PDF或Word文档');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setMessage('');
      
      if (fileType === 'video') {
        const url = URL.createObjectURL(file);
        setVideoUrl(url);
        setPosterImage(null);
      } else if (fileType === 'audio') {
        setVideoUrl(null);
      }
    }
  };

  const captureFrame = () => {
    console.log('=== captureFrame 被调用 ===');
    console.log('videoRef.current:', videoRef.current);
    console.log('canvasRef.current:', canvasRef.current);
    
    if (!videoRef.current || !canvasRef.current) {
      console.log('缺少 video 或 canvas 引用');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    console.log('视频尺寸:', video.videoWidth, 'x', video.videoHeight);
    
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    console.log('生成的图片数据长度:', imageData.length);
    console.log('图片数据前100字符:', imageData.substring(0, 100));
    
    setPosterImage(imageData);
    setMessage('✓ 截图成功！');
  };

  const extractFrameFromServer = async () => {
    if (!selectedFile || !videoRef.current) return;
    
    setExtractingFrame(true);
    try {
      const formData = new FormData();
      formData.append('video', selectedFile);
      formData.append('time', currentTime.toString());
      
      const response = await fetch('/api/extract-frame', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPosterImage(imageUrl);
        setMessage('帧提取成功！');
      } else {
        setMessage('帧提取失败，请使用本地截图功能');
      }
    } catch (error) {
      console.error('Error extracting frame:', error);
      setMessage('帧提取失败，请使用本地截图功能');
    } finally {
      setExtractingFrame(false);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleSubtitleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.srt') && !fileName.endsWith('.vtt')) {
        setMessage('请选择SRT或VTT字幕文件');
        setSelectedSubtitleFile(null);
        return;
      }
      setSelectedSubtitleFile(file);
      setMessage('字幕文件已选择');
    }
  };

  const handlePosterFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setMessage('请选择图片文件');
        setSelectedPosterFile(null);
        setPosterImage(null);
        return;
      }
      setSelectedPosterFile(file);
      const url = URL.createObjectURL(file);
      setPosterImage(url);
      setMessage('封面图片已选择');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setMessage('请选择文件');
      return;
    }

    if (!title) {
      setMessage('请输入标题');
      return;
    }
    
    setUploading(true);
    setMessage('');
    setProgress(0);

    try {
      let fileUrl = null;
      let subtitleUrl = null;
      let vocabularyAnalysis = null;
      let finalLevel = level;
      
      const formData = new FormData();
      
      // 根据文件类型添加不同的字段名
      if (fileType === 'video') {
        formData.append('video', selectedFile);
      } else if (fileType === 'audio') {
        formData.append('audio', selectedFile);
      } else {
        formData.append('document', selectedFile);
      }
      
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('subcategory', subcategory);
      formData.append('fileType', fileType);
      
      if (selectedSubtitleFile) {
        formData.append('subtitle', selectedSubtitleFile);
      }
      
      // 如果用户选择了封面图或者截图了封面，上传
      if (selectedPosterFile) {
        formData.append('poster', selectedPosterFile);
        console.log('用户上传的封面已添加到上传表单');
      } else if (posterImage && posterImage.length > 0) {
        try {
          // 将 base64 转换为 blob
          const response = await fetch(posterImage);
          const blob = await response.blob();
          // 创建文件对象
          const posterFile = new File([blob], `poster-${Date.now()}.jpg`, { type: 'image/jpeg' });
          formData.append('poster', posterFile);
          console.log('用户截图封面已添加到上传表单');
        } catch (convertError) {
          console.error('转换封面图片失败:', convertError);
        }
      }
      
      const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/upload-multiple`, {
        method: 'POST',
        body: formData
      });
      
      // 从后端获取视频时长（秒）
      let videoDuration = 0;
      
      console.log('上传响应状态:', uploadResponse.status, uploadResponse.ok);
      
      if (!uploadResponse.ok) {
        console.log('后端上传失败，状态码:', uploadResponse.status);
        const errorText = await uploadResponse.text();
        console.log('错误响应:', errorText);
        if (fileType === 'video') {
          fileUrl = '/uploads/1772348648817-Overview of Cell Structure.mp4';
        } else if (fileType === 'audio') {
          fileUrl = '/uploads/default-audio.mp3';
        }
        if (selectedSubtitleFile) {
          subtitleUrl = '/uploads/1772626289226-Overview of Cell Structure.srt';
        }
        videoDuration = 300; // 默认5分钟
        setProgress(70);
      } else {
        const uploadData = await uploadResponse.json();
        console.log('上传响应数据:', uploadData);
        
        // 根据文件类型获取对应的URL
        if (fileType === 'video') {
          fileUrl = uploadData.files?.videoUrl;
        } else if (fileType === 'audio') {
          fileUrl = uploadData.files?.audioUrl;
        } else {
          fileUrl = uploadData.files?.documentUrl;
        }
        subtitleUrl = uploadData.files?.subtitleUrl;
        
        // 从后端获取真实的时长（秒）
        if (uploadData.videos && uploadData.videos.length > 0) {
          const uploadedVideo = uploadData.videos[0];
          console.log('获取到的文件信息:', uploadedVideo);
          if (uploadedVideo.duration && uploadedVideo.duration > 0) {
            videoDuration = uploadedVideo.duration;
            console.log('从后端获取的真实时长:', videoDuration, '秒');
          } else {
            console.log('后端返回的时长为0或不存在，使用默认值');
            videoDuration = 300; // 默认5分钟
          }
        } else {
          console.log('后端未返回文件列表，使用默认值');
          videoDuration = 300; // 默认5分钟
        }
        
        setProgress(70);
      }
      
      if (selectedSubtitleFile) {
        setMessage('正在分析字幕词汇...');
        try {
          const subtitleContent = await selectedSubtitleFile.text();
          const analyzeResponse = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vocabulary/analyze`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ subtitleContent })
          });
          
          if (analyzeResponse.ok) {
            const analyzeData = await analyzeResponse.json();
            if (analyzeData.success && analyzeData.analysis) {
              vocabularyAnalysis = analyzeData.analysis;
              setVocabularyAnalysis(analyzeData.analysis);
              finalLevel = analyzeData.analysis.recommendedLevel;
              console.log('词汇分析结果:', vocabularyAnalysis);
              console.log('推荐级别:', finalLevel);
            }
          }
        } catch (analyzeError) {
          console.error('词汇分析失败:', analyzeError);
        }
      }
      setProgress(85);
      
      let videoPosterUrl = null;
      if (posterImage && posterImage.length > 0) {
        videoPosterUrl = posterImage;
      }
      
      const uploadedVideos = JSON.parse((localStorage.getItem('uploadedVideos') || '[]').replace(/^"undefined"$/, '[]'));
      const newVideo = {
        id: Date.now().toString(),
        title: title,
        description: description,
        category: category,
        subcategory: subcategory,
        level: finalLevel,
        duration: videoDuration, // 使用从后端获取的真实时长（秒）
        fileUrl: fileUrl,
        subtitleUrl: subtitleUrl,
        posterUrl: videoPosterUrl,
        fileType: fileType,
        hidden: false,
        uploadedAt: new Date().toISOString(),
        vocabularyAnalysis: vocabularyAnalysis
      };
      uploadedVideos.push(newVideo);
      localStorage.setItem('uploadedVideos', JSON.stringify(uploadedVideos));
      
      setProgress(100);
      if (vocabularyAnalysis) {
        setMessage(`上传成功！视频难度已自动设置为 ${finalLevel}`);
      } else {
        setMessage('上传成功！视频已添加到课程列表');
      }
      
      setTimeout(() => {
        navigate('/admin/manage');
      }, 2000);
    } catch (error) {
      console.error('Upload error:', error);
      setMessage('上传失败，请重试');
    } finally {
      setUploading(false);
      
      setSelectedFile(null);
      setSelectedSubtitleFile(null);
      setTitle('');
      setDescription('');
      setVideoUrl(null);
      setPosterImage(null);
      setProgress(0);
      setVocabularyAnalysis(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const categories = [
    { value: 'universe', label: '宇宙与空间科学' },
    { value: 'physics', label: '物理学' },
    { value: 'chemistry', label: '化学' },
    { value: 'earth', label: '地球科学' },
    { value: 'biology', label: '基础生物学' },
    { value: 'plants', label: '植物学' },
    { value: 'animals', label: '动物学' },
    { value: 'microbes', label: '微生物学' },
    { value: 'ecology', label: '生态学' },
    { value: 'human', label: '人体科学' }
  ];

  // 各分类的详细子分类
  const subcategories = {
    universe: [
      { value: 'stars_galaxies_blackholes', label: '恒星、星系与黑洞 - Stars, Galaxies & Black Holes' },
      { value: 'solar_system', label: '太阳系 - The Solar System' },
      { value: 'moon_space_exploration', label: '月球与太空探索 - The Moon & Space Exploration' },
      { value: 'astronomy_observation', label: '天文观测 - Astronomy & Sky Watching' }
    ],
    physics: [
      { value: 'forces_motion', label: '力与运动 - Forces & Motion' },
      { value: 'energy', label: '能量 - Energy（含热、电、简单电路）' },
      { value: 'sound_light', label: '声与光 - Sound & Light' },
      { value: 'magnetism', label: '磁 - Magnetism' }
    ],
    chemistry: [
      { value: 'states_properties', label: '物质的状态与性质 - States & Properties of Matter' },
      { value: 'mixtures_separation', label: '混合物与分离 - Mixtures & Separation' },
      { value: 'acids_bases_reactions', label: '酸碱与反应 - Acids, Bases & Reactions' },
      { value: 'elements_periodic_table', label: '元素与周期表 - Elements & The Periodic Table' },
      { value: 'everyday_chemistry', label: '生活中的化学 - Chemistry in Everyday Life' }
    ],
    earth: [
      { value: 'rocks_fossils_geology', label: '岩石、化石与地质 - Rocks, Fossils & Geology' },
      { value: 'water_cycle_oceans', label: '水循环与海洋 - Water Cycle & Oceans' },
      { value: 'weather_climate', label: '天气与气候 - Weather & Climate' },
      { value: 'maps_globes', label: '地图与地球仪 - Maps & Globes' },
      { value: 'soil_land', label: '土壤与陆地 - Soil & Land' }
    ],
    biology: [
      { value: 'cells_genetics', label: '细胞与遗传 - Cells & Genetics' },
      { value: 'life_processes', label: '生命过程 - Life Processes（生命周期、进化）' },
      { value: 'ecology', label: '生态关系 - Ecology（食物链、栖息地）' },
      { value: 'classification_adaptations', label: '生物分类与适应 - Classification & Adaptations' },
      { value: 'humans_conservation', label: '人类与自然保护 - Humans & Conservation' }
    ],
    plants: [
      { value: 'seeds_growth', label: '种子与生长 - Seeds & Growth' },
      { value: 'flowers_pollination', label: '花与授粉 - Flowers & Pollination' },
      { value: 'photosynthesis_nutrition', label: '光合作用与养分 - Photosynthesis & Nutrition' },
      { value: 'plant_defenses_carnivorous', label: '植物的防御与奇特植物 - Plant Defenses & Carnivorous Plants' },
      { value: 'fungi_useful_plants', label: '真菌与人类使用的植物 - Fungi & Useful Plants' }
    ],
    animals: [
      { value: 'animal_classification', label: '动物分类 - Animal Classification（哺乳动物、鸟类、爬行类、两栖类、鱼类、无脊椎动物）' },
      { value: 'insects_spiders', label: '昆虫与蜘蛛 - Insects & Spiders' },
      { value: 'ocean_animals', label: '海洋动物 - Ocean Animals' },
      { value: 'animal_behaviors_life_cycles', label: '动物行为与生命周期 - Animal Behaviors & Life Cycles' },
      { value: 'endangered_animals_conservation', label: '濒危动物保护 - Endangered Animals & Conservation' }
    ],
    microbes: [
      { value: 'types_of_microbes', label: '微生物的种类 - Types of Microbes（细菌、病毒、真菌、原生生物）' },
      { value: 'habitats_of_microbes', label: '微生物的栖息地 - Habitats of Microbes' },
      { value: 'helpful_harmful_microbes', label: '有益与有害微生物 - Helpful & Harmful Microbes' },
      { value: 'microbes_food', label: '微生物与食物 - Microbes & Food' },
      { value: 'microscopes_observation', label: '显微镜与观察 - Microscopes & Observation' }
    ],
    ecology: [
      { value: 'land_ecosystems', label: '陆地生态 - Land Ecosystems（森林、沙漠、草原、极地）' },
      { value: 'water_ecosystems', label: '水域生态 - Water Ecosystems（海洋、淡水）' },
      { value: 'urban_ecology', label: '城市生态 - Urban Ecology' },
      { value: 'conservation', label: '生态保护 - Conservation（回收、生物多样性、可持续发展）' },
      { value: 'ecological_processes', label: '生态过程 - Ecological Processes（食物网、人类影响）' }
    ],
    human: [
      { value: 'skeletal_muscular_system', label: '骨骼与肌肉系统 - Skeletal & Muscular System' },
      { value: 'circulatory_respiratory_system', label: '循环与呼吸系统 - Circulatory & Respiratory System' },
      { value: 'digestive_endocrine_system', label: '消化与内分泌 - Digestive & Endocrine System' },
      { value: 'senses_nervous_system', label: '感官与神经系统 - Senses & Nervous System' },
      { value: 'health_disease', label: '健康与疾病 - Health & Disease' }
    ]
  };

  const levels = [
    { value: 'Starters', label: 'Starters' },
    { value: 'Movers', label: 'Movers' },
    { value: 'Flyers', label: 'Flyers' },
    { value: 'KET', label: 'KET' },
    { value: 'PET', label: 'PET' },
    { value: 'FCE', label: 'FCE' }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', color: '#1a2b4c' }}>
      {/* 顶部导航栏 */}
      <nav style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        padding: '1rem 0',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div className="container" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            textDecoration: 'none',
            color: '#1a2b4c'
          }}>
            <img 
              src="/logo.png" 
              alt="123wonder英文科普" 
              style={{ 
                height: '50px', 
                width: 'auto'
              }} 
            />
            <div>
              <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600', color: '#1a2b4c' }}>管理员后台</h1>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#6c757d' }}>文件上传管理</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button 
              onClick={() => navigate('/admin/manage')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              管理文件
            </button>
            <button 
              onClick={() => navigate('/admin/vocabulary')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              词汇管理
            </button>
            <button 
              onClick={() => navigate('/admin/invitation')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              邀请码管理
            </button>
            <button 
              onClick={() => navigate('/admin/users')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              用户管理
            </button>
            <span style={{ color: '#495057', fontSize: '1.1rem' }}>欢迎，{user?.username}</span>
            <button 
              onClick={handleLogout}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <FiLogOut /> 退出
            </button>
          </div>
        </div>
      </nav>

      <div style={{ padding: '2rem 0', backgroundColor: '#f8f9fa' }}>
        <div className="container" style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1.5rem'
        }}>
          <button 
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: '1px solid #e9ecef',
              color: '#1a2b4c',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FiHome /> 返回首页
          </button>

          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h2 style={{ 
              marginBottom: '2rem',
              color: '#1a2b4c',
              fontSize: '1.8rem',
              fontWeight: '700'
            }}>文件上传</h2>

            {message && (
              <div style={{ 
                backgroundColor: message.includes('成功') ? '#d4edda' : '#f8d7da',
                color: message.includes('成功') ? '#155724' : '#721c24',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: message.includes('成功') ? '1px solid #c3e6cb' : '1px solid #f5c6cb'
              }}>
                {message}
              </div>
            )}
            
            {vocabularyAnalysis && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem', fontWeight: '600' }}>词汇分布统计</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f1f1' }}>
                        <th style={{ padding: '0.5rem', border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: '600' }}>Starters</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: '600' }}>Movers</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: '600' }}>Flyers</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: '600' }}>KET</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: '600' }}>PET</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: '600' }}>FCE</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {(() => {
                          const levelCounts = vocabularyAnalysis.levelCounts;
                          const maxCount = Math.max(...Object.values(levelCounts).filter(count => count !== levelCounts.Other));
                          return [
                            { name: 'Starters', count: levelCounts.Starters },
                            { name: 'Movers', count: levelCounts.Movers },
                            { name: 'Flyers', count: levelCounts.Flyers },
                            { name: 'KET', count: levelCounts.KET },
                            { name: 'PET', count: levelCounts.PET },
                            { name: 'FCE', count: levelCounts.FCE }
                          ].map((level) => (
                            <td 
                              key={level.name} 
                              style={{
                                padding: '0.5rem', 
                                border: '1px solid #e0e0e0', 
                                textAlign: 'center',
                                ...(level.count === maxCount ? { 
                                  backgroundColor: 'rgba(26, 115, 232, 0.1)', 
                                  color: '#1a73e8', 
                                  fontWeight: '600' 
                                } : {})
                              }}
                            >
                              {level.count}
                            </td>
                          ));
                        })()}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                  其他词汇: {vocabularyAnalysis.levelCounts.Other}词
                </div>
              </div>
            )}

            <form onSubmit={handleUpload}>
              {/* 文件类型选择 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#495057',
                  fontWeight: '500'
                }}>文件类型</label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    backgroundColor: fileType === 'video' ? '#e3f2fd' : '#f8f9fa',
                    border: fileType === 'video' ? '1px solid #bbdefb' : '1px solid #e9ecef',
                    cursor: 'pointer',
                    flex: 1,
                    transition: 'all 0.3s ease'
                  }}>
                    <FiVideo style={{ color: '#1976d2' }} />
                    <span>视频</span>
                    <input 
                      type="radio" 
                      name="fileType" 
                      value="video" 
                      checked={fileType === 'video'}
                      onChange={() => setFileType('video')}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    backgroundColor: fileType === 'audio' ? '#e3f2fd' : '#f8f9fa',
                    border: fileType === 'audio' ? '1px solid #bbdefb' : '1px solid #e9ecef',
                    cursor: 'pointer',
                    flex: 1,
                    transition: 'all 0.3s ease'
                  }}>
                    <FiCamera style={{ color: '#1976d2' }} />
                    <span>音频</span>
                    <input 
                      type="radio" 
                      name="fileType" 
                      value="audio" 
                      checked={fileType === 'audio'}
                      onChange={() => setFileType('audio')}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    backgroundColor: fileType === 'document' ? '#e3f2fd' : '#f8f9fa',
                    border: fileType === 'document' ? '1px solid #bbdefb' : '1px solid #e9ecef',
                    cursor: 'pointer',
                    flex: 1,
                    transition: 'all 0.3s ease'
                  }}>
                    <FiFileText style={{ color: '#1976d2' }} />
                    <span>文档</span>
                    <input 
                      type="radio" 
                      name="fileType" 
                      value="document" 
                      checked={fileType === 'document'}
                      onChange={() => setFileType('document')}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              {/* 文件选择 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#495057',
                  fontWeight: '500'
                }}>选择文件</label>
                <div style={{ 
                  border: '2px dashed #dee2e6',
                  borderRadius: '8px',
                  padding: '3rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: '#f8f9fa'
                }} onDragOver={(e) => e.preventDefault()}
                   onDrop={(e) => {
                     e.preventDefault();
                     const file = e.dataTransfer.files[0];
                     if (file) {
                       const event = { target: { files: [file] } };
                       handleFileChange(event);
                     }
                   }}
                   onClick={() => document.getElementById('fileInput').click()}>
                  <input 
                    id="fileInput"
                    type="file" 
                    accept={fileType === 'video' ? 'video/*' : fileType === 'audio' ? 'audio/*' : '.pdf,.doc,.docx'}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <FiUpload style={{ fontSize: '3rem', marginBottom: '1rem', color: '#adb5bd' }} />
                  <p style={{ color: '#495057', marginBottom: '0.5rem' }}>
                    {selectedFile ? selectedFile.name : `点击或拖拽${fileType === 'video' ? '视频' : fileType === 'audio' ? '音频' : '文档'}文件到此处`}
                  </p>
                  <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                    {fileType === 'video' ? '支持 MP4, MOV, AVI 等格式' : fileType === 'audio' ? '支持 MP3, WAV, M4A 等格式' : '支持 PDF, Word 格式'}
                  </p>
                </div>
              </div>

              {/* 字幕文件选择（视频和音频时显示） */}
              {(fileType === 'video' || fileType === 'audio') && (
                <>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: '#495057',
                      fontWeight: '500'
                    }}>选择字幕文件（可选）</label>
                    <div style={{ 
                      border: '2px dashed #dee2e6',
                      borderRadius: '8px',
                      padding: '2rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      backgroundColor: selectedSubtitleFile ? '#e3f2fd' : '#f8f9fa'
                    }} onDragOver={(e) => e.preventDefault()}
                       onDrop={(e) => {
                         e.preventDefault();
                         const file = e.dataTransfer.files[0];
                         if (file) {
                           const event = { target: { files: [file] } };
                           handleSubtitleFileChange(event);
                         }
                       }}
                       onClick={() => document.getElementById('subtitleFileInput').click()}>
                      <input 
                        id="subtitleFileInput"
                        type="file" 
                        accept=".srt,.vtt"
                        onChange={handleSubtitleFileChange}
                        style={{ display: 'none' }}
                      />
                      <FiFileText style={{ fontSize: '2.5rem', marginBottom: '0.8rem', color: selectedSubtitleFile ? '#1976d2' : '#adb5bd' }} />
                      <p style={{ color: '#495057', marginBottom: '0.5rem' }}>
                        {selectedSubtitleFile ? selectedSubtitleFile.name : '点击或拖拽字幕文件到此处（SRT/VTT格式）'}
                      </p>
                      <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                        支持 SRT, VTT 字幕格式
                      </p>
                    </div>
                  </div>
                  
                  {/* 视频播放器和封面选择 */}
                  {videoUrl && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '0.5rem',
                        color: '#495057',
                        fontWeight: '500'
                      }}>视频预览和封面选择（重要：必须选择封面！）</label>
                      
                      {!posterImage && (
                        <div style={{ 
                          backgroundColor: '#fff3cd', 
                          color: '#856404',
                          padding: '1rem',
                          borderRadius: '8px',
                          marginBottom: '1rem',
                          border: '1px solid #ffeeba',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                          <span><strong>提示：</strong>请播放视频，选择一个合适的画面，然后点击「本地截图」按钮来设置视频封面！</span>
                        </div>
                      )}
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        {/* 视频播放器 */}
                        <div>
                          <video
                            ref={videoRef}
                            src={videoUrl}
                            controls
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={() => {
                              console.log('视频已加载，自动尝试截图');
                              setTimeout(() => {
                                if (!posterImage && videoRef.current) {
                                  captureFrame();
                                }
                              }, 1000);
                            }}
                            style={{
                              width: '100%',
                              borderRadius: '8px',
                              backgroundColor: '#000'
                            }}
                          />
                          <p style={{ color: '#6c757d', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            当前时间: {currentTime.toFixed(1)}秒
                          </p>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <button
                              type="button"
                              onClick={captureFrame}
                              style={{
                                flex: 1,
                                padding: '1rem',
                                borderRadius: '8px',
                                backgroundColor: posterImage ? '#4caf50' : '#ff9800',
                                border: 'none',
                                color: 'white',
                                fontSize: '1rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                              }}
                              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                            >
                              <FiCamera size={20} /> {posterImage ? '✓ 已截图 - 重新截图' : '📸 点击截图设置封面'}
                            </button>
                          </div>
                        </div>
                        
                        {/* 封面预览 */}
                        <div>
                          <label style={{ 
                            display: 'block',
                            marginBottom: '0.5rem',
                            color: '#495057',
                            fontWeight: '500'
                          }}>
                            {posterImage ? <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#4caf50' }}><FiCheck style={{ color: '#4caf50' }} /> 封面已设置 ✓</span> : '封面预览'}
                          </label>
                          {posterImage ? (
                            <div style={{
                              width: '100%',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '3px solid #4caf50',
                              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                            }}>
                              <img 
                                src={posterImage} 
                                alt="视频封面"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block'
                                }}
                              />
                            </div>
                          ) : (
                            <div style={{
                              width: '100%',
                              aspectRatio: '16/9',
                              borderRadius: '8px',
                              backgroundColor: '#fff3cd',
                              border: '3px dashed #ffc107',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#856404',
                              padding: '1rem',
                              textAlign: 'center'
                            }}>
                              <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>📷 还没有设置封面</p>
                              <p style={{ fontSize: '0.9rem' }}>播放视频后点击「点击截图设置封面」</p>
                            </div>
                          )}
                        </div>
                      </div>
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
              )}

              {/* 音频文件封面图上传 */}
              {fileType === 'audio' && selectedFile && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>音频封面图片（必须上传！）</label>
                  
                  {!posterImage && (
                    <div style={{ 
                      backgroundColor: '#fff3cd', 
                      color: '#856404',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      border: '1px solid #ffeeba',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                      <span><strong>提示：</strong>请上传一张封面图片作为播客的封面！</span>
                    </div>
                  )}
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {/* 封面图上传 */}
                    <div>
                      <div style={{ 
                        border: '2px dashed #dee2e6',
                        borderRadius: '8px',
                        padding: '2rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: selectedPosterFile ? '#e3f2fd' : '#f8f9fa'
                      }} onDragOver={(e) => e.preventDefault()}
                         onDrop={(e) => {
                           e.preventDefault();
                           const file = e.dataTransfer.files[0];
                           if (file) {
                             const event = { target: { files: [file] } };
                             handlePosterFileChange(event);
                           }
                         }}
                         onClick={() => document.getElementById('posterFileInput').click()}>
                        <input 
                          id="posterFileInput"
                          type="file" 
                          accept="image/*"
                          onChange={handlePosterFileChange}
                          style={{ display: 'none' }}
                        />
                        <FiCamera style={{ fontSize: '2.5rem', marginBottom: '0.8rem', color: selectedPosterFile ? '#1976d2' : '#adb5bd' }} />
                        <p style={{ color: '#495057', marginBottom: '0.5rem' }}>
                          {selectedPosterFile ? selectedPosterFile.name : '点击或拖拽封面图片到此处'}
                        </p>
                        <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                          支持 JPG, PNG 等图片格式
                        </p>
                      </div>
                    </div>
                    
                    {/* 封面预览 */}
                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '0.5rem',
                        color: '#495057',
                        fontWeight: '500'
                      }}>
                        {posterImage ? <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#4caf50' }}><FiCheck style={{ color: '#4caf50' }} /> 封面已设置 ✓</span> : '封面预览'}
                      </label>
                      {posterImage ? (
                        <div style={{
                          width: '100%',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '3px solid #4caf50',
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                        }}>
                          <img 
                            src={posterImage} 
                            alt="音频封面"
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block'
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{
                          width: '100%',
                          aspectRatio: '16/9',
                          borderRadius: '8px',
                          backgroundColor: '#fff3cd',
                          border: '3px dashed #ffc107',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#856404',
                          padding: '1rem',
                          textAlign: 'center'
                        }}>
                          <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>📷 还没有设置封面</p>
                          <p style={{ fontSize: '0.9rem' }}>请上传一张封面图片</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

              {/* 进度条 */}
              {uploading && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ 
                    height: '8px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      height: '100%',
                      width: `${progress}%`,
                      backgroundColor: '#1976d2',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <p style={{ color: '#6c757d', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                    上传中... {progress}%
                  </p>
                </div>
              )}

              {/* 基本信息 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>标题</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                    placeholder="请输入标题"
                  />
                </div>
                
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>分类</label>
                  <select
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      // 重置详细分类
                      setSubcategory('');
                    }}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* 详细分类 */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>详细分类</label>
                  <select
                    value={subcategory}
                    onChange={(e) => setSubcategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">请选择详细分类</option>
                    {(subcategories[category] || []).map(subcat => (
                      <option key={subcat.value} value={subcat.value}>{subcat.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>难度</label>
                  <select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {levels.map(lev => (
                      <option key={lev.value} value={lev.value}>{lev.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* 文件类型 */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>文件类型</label>
                  <select
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="video">视频</option>
                    <option value="audio">音频</option>
                    <option value="document">文档</option>
                  </select>
                </div>
                

              </div>

              {/* 描述 */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#495057',
                  fontWeight: '500'
                }}>描述</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    resize: 'vertical',
                    transition: 'all 0.3s ease'
                  }}
                  placeholder="请输入描述"
                ></textarea>
              </div>

              {/* 提交按钮 */}
              <button 
                type="submit"
                disabled={uploading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  borderRadius: '6px',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: uploading ? 0.7 : 1
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
              >
                {uploading ? '上传中...' : '上传文件'}
              </button>
            </form>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminUpload;